import { Box, ButtonProps, Flex, Text } from "@chakra-ui/react";
import { MediaThumbnail } from "@/shared/components/molecules/MediaThumbnail/MediaThumbnail";
import { useUser } from "@/providers/useUser";
import { UserRole, EntityName } from "@/schemaTypes";
import { GuaranteeBox } from "@/pages/academy/Programs/components/programWarante";
import { PurchaseProgramCard } from "@/features/products/components/organism/PurchaseProgramCard";
import { PurchaseCard } from "@/features/products/components/organism/PurchaseCard/PurchaseCard";
import { mainBrands } from "@/pages/academy/Learn/brandFilter/data";

interface BootcampEnrollPanelProps {
  thumbnailUrl: string;
  thumbnailVideoUrl?: string;
  onEnroll: () => void;
  remainingSlots: number;
  brandName?: string;
  isUserSubscribed?: boolean;
  videoUrl?: string;
  buttonProps?: ButtonProps;
  isRegisteredInBootcamp: boolean | null;
  productPrice?: number | null;
  productCurrency?: string;
  bootcampType?: string | null;
  isOpenPurchase?: boolean;
  onlyCta?: boolean;
  basePrice?: number;
  cohortId?: string | null;
}

export const BootcampEnrollPanel: React.FC<BootcampEnrollPanelProps> = ({
  thumbnailUrl,
  thumbnailVideoUrl,
  videoUrl,
  onEnroll,
  remainingSlots,
  buttonProps,
  brandName,
  isRegisteredInBootcamp,
  //la logica esta solo para cuando el BC tiene precio
  productPrice,
  productCurrency,
  bootcampType,
  isOpenPurchase,
  onlyCta = false,
  basePrice,
  cohortId,
}) => {
  const { user, hasRoles } = useUser();
  const isMastermind = bootcampType === EntityName.MastermindBootcamp;
  const isPremium = Boolean(user) && hasRoles([UserRole.PremiumSubscriber]);
  const showPrice = Boolean(isOpenPurchase) || isMastermind || !isPremium;
  const hasUsablePrice =
    typeof productPrice === "number" && !Number.isNaN(productPrice);

  const isPriceLoading = showPrice && !hasUsablePrice;

  const icon = mainBrands.find((brand) => brand.name === brandName)?.icon;
  const features = [
    "Acceso vitalicio a las grabaciones",
    "Acceso a tutor académico",
    "Recursos descargables",
    "⁠⁠Garantía de satisfacción",
  ];
  const availableSlots = Math.max(remainingSlots, 0);
  const EXCLUDED_COHORT_ID = "69729d1a482a314c829e7ac5";

  const excludedFeaturesForCohort = new Set([
    "Acceso a tutor académico",
    "Recursos descargables",
  ]);

  const finalFeatures =
    cohortId === EXCLUDED_COHORT_ID
      ? features.filter((f) => !excludedFeaturesForCohort.has(f))
      : features;

  if (isRegisteredInBootcamp) {
    return (
      <Flex
        flexDir="column"
        gap={6}
        id="bootcamp-enroll-panel-registered"
        data-testid="bootcamp-enroll-panel-registered"
      >
        {!onlyCta && (
          <MediaThumbnail
            thumbnailUrl={thumbnailUrl}
            badge={icon}
            badgeSize="40px"
          />
        )}
        <Box
          borderWidth="1px"
          borderColor="neutral.100"
          borderRadius="6px"
          bg="white"
          p={4}
          data-testid="bootcamp-enroll-registered-message"
        >
          <Text
            fontFamily="Poppins"
            fontSize="16px"
            fontWeight={600}
            color="secondary.300"
          >
            Ya estás registrado en este bootcamp
          </Text>
          <Text fontSize="14px" color="neutral.400" mt={2}>
            Revisa tu agenda para unirte a las próximas sesiones en vivo.
          </Text>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex flexDir="column" gap={6} data-testid="bootcamp-enroll-panel">
      {!onlyCta && (
        <MediaThumbnail
          thumbnailUrl={thumbnailUrl}
          thumbnailVideoUrl={thumbnailVideoUrl}
          videoUrl={videoUrl}
          badge={icon}
          badgeSize="40px"
        />
      )}

      {!onlyCta && (
        <Flex
          align="center"
          justify="center"
          borderRadius="8px"
          py={2}
          border="1px solid #FFD7D8"
          data-testid="bootcamp-available-slots"
        >
          <Text
            fontFamily="Inter"
            fontWeight={600}
            fontSize="12px"
            color="#F00004"
            textTransform="uppercase"
            letterSpacing="0.08em"
          >
            {availableSlots > 0
              ? `Quedan ${availableSlots} cupos`
              : "Cupos agotados"}
          </Text>
        </Flex>
      )}

      {showPrice ? (
        <PurchaseCard
          title="Compra este programa"
          description="Accede al programa completo con un solo pago."
          pricing={{
            kind: "one-time",
            amount: {
              amount: hasUsablePrice ? productPrice! : 0,
              currency: productCurrency ?? "USD",
            },
          }}
          onAction={onEnroll}
          isLoading={isPriceLoading}
          isActionLoading={buttonProps?.isLoading ?? isPriceLoading}
          isActionDisabled={
            (buttonProps?.isDisabled ?? false) || availableSlots <= 0
          }
        />
      ) : (
        // Premium subscribers see a registration panel with no price at all.
        // The new design has no price-less variant, so this state stays on the
        // previous card until one exists.
        <PurchaseProgramCard
          title="Compra este programa"
          currentPrice={0}
          originalPrice={basePrice}
          currency={productCurrency ?? "USD"}
          features={finalFeatures}
          includeStartCTA={false}
          onLinkNow={onEnroll}
          onSecondaryAction={() => undefined}
          isLinkLoading={buttonProps?.isLoading ?? isPriceLoading}
          primaryCtaLabel="¡Regístrate ahora!"
          isPrimaryDisabled={
            (buttonProps?.isDisabled ?? false) || availableSlots <= 0
          }
          showPrice={false}
          isPriceLoading={isPriceLoading}
          showSelectTag={isOpenPurchase === true}
        />
      )}
      <GuaranteeBox
        title="Garantía de Satisfacción*"
        description="Si este programa no cumple con tus expectativas, te devolvemos tu dinero."
      />
    </Flex>
  );
};
